import {
  BnplFeePayer,
  CancellationWaitlistNotifyMode,
  EmployeesToServices,
  IntakeQuestions,
  PiercingLocations,
  ReschedulePolicy,
  ReschedulePolicyBehavior,
  ServiceCategory,
  Services,
  ShopOnboardings,
  ShopOnboardingSteps,
  Waivers,
} from '@prisma/client';
import * as yup from 'yup';
import { convertToRaw } from 'draft-js';
import type Stripe from 'stripe';
import { getUrlSchema } from '../global';
import { testMaxDecimalPlaces } from '../../utils/global/helper';
import TableDropdownMenu from '../../components/global/TableDropdownMenu';

export type SettingsAction =
  | {
      type: 'button';
      text: string;
      onClick: () => void | Promise<void>;
      isLoading?: boolean;
      disabled?: boolean;
    }
  | {
      type: 'switch';
      checked: boolean;
      onChange: (checked: boolean) => void | Promise<void>;
      disabled?: boolean;
    };

export type SettingsRow = {
  isLabelUserInput?: boolean;
  action?: SettingsAction;
  dropdown?: React.ComponentProps<typeof TableDropdownMenu>;
} & (
  | {
      title: string;
      label: string | React.ReactNode;
      tooltip?: string;
    }
  | {
      id: string;
      component: React.ReactNode;
    }
);

export const PERCENT_MAX_TAX_DECIMAL_PLACES = 3; // allow something like 8.625%
export const RATE_MAX_TAX_DECIMAL_PLACES = 5; // allow something like 0.08625

export const businessTaxRatesSchema = yup.object({
  serviceTaxRate: yup
    .number()
    .min(0, 'Tax rate must be greater than 0')
    .max(100, 'Tax rate must be less than 100')
    .test(
      'max-decimal-places',
      `Tax rate must be less than ${PERCENT_MAX_TAX_DECIMAL_PLACES} decimal places`,
      value => testMaxDecimalPlaces(value || 0, PERCENT_MAX_TAX_DECIMAL_PLACES),
    )
    .required(),
  productTaxRate: yup
    .number()
    .min(0, 'Tax rate must be greater than 0')
    .max(100, 'Tax rate must be less than 100')
    .test(
      'max-decimal-places',
      `Tax rate must be less than ${PERCENT_MAX_TAX_DECIMAL_PLACES} decimal places`,
      value => testMaxDecimalPlaces(value || 0, PERCENT_MAX_TAX_DECIMAL_PLACES),
    )
    .required(),
});

export const updateBusinessSchema = yup.object({
  name: yup.string().trim().min(1, "Name can't be empty").optional(),
  streetAddress: yup
    .string()
    .trim()
    .min(1, "Street address can't be empty")
    .optional(),
  streetAddress2: yup.string().trim().optional().nullable(),
  city: yup.string().trim().min(1, "City can't be empty").optional(),
  state: yup.string().trim().min(1, "State can't be empty").optional(),
  zip: yup
    .string()
    .trim()
    .min(1, "Zip can't be empty")
    .matches(/^[0-9]*$/, 'Zip must be a number')
    .optional(),
  thumbnailImage: yup
    .string()
    .trim()
    .url('Thumbnail image must be a valid URL')
    .optional(),
  coverImage: yup
    .string()
    .trim()
    .url('Cover image must be a valid URL')
    .optional(),
  urlPath: yup.string().trim().optional(),
  reviewLink: getUrlSchema('Review link must be a valid URL').optional(),
  reviewLinksEnabled: yup.boolean().optional(),
  allowGeneralRequests: yup.boolean().optional(),
  generalRequestManagerIds: yup
    .array()
    .of(yup.string())
    .min(1, 'At least one manager is required')
    .optional()
    .optional(),
  tattooAftercareLink: getUrlSchema(
    'Aftercare link must be a valid URL',
  ).optional(),
  stationMax: yup
    .number()
    .integer('Number of stations must be a whole number')
    .min(0, 'Number of stations must be a non-negative number')
    .nullable()
    .optional(),
  shopPolicyRaw: yup
    .mixed<ReturnType<typeof convertToRaw>>()
    .nullable()
    .optional(),
  allowMinors: yup.boolean().optional(),
  minimumTattooAgeInYears: yup
    .number()
    .integer('Minimum age must be a whole number')
    .min(0, 'Minimum age must be at least 0')
    .max(17, 'Minimum age must be less than 18')
    .optional(),
  requireMinorIdentification: yup.boolean().optional(),
  requireMinorBirthCertificate: yup.boolean().optional(),
  requireMinorGuardianIdentification: yup.boolean().optional(),
  requireMinorGuardianPresent: yup.boolean().optional(),
});

export const addBusinessTripSchema = yup.object({
  studioName: yup.string().trim().min(1, "Name can't be empty").optional(),
  streetAddress: yup
    .string()
    .trim()
    .min(1, "Street address can't be empty")
    .optional(),
  streetAddress2: yup.string().trim().optional().nullable(),
  city: yup.string().trim().min(1, "City can't be empty").optional(),
  state: yup.string().trim().min(1, "State can't be empty").optional(),
  zip: yup
    .string()
    .trim()
    .min(1, "Zip can't be empty")
    .matches(/^[0-9]*$/, 'Zip must be a number')
    .optional(),
  timezone: yup.string().trim().min(1, "Timezone can't be empty").optional(),
  startDateTime: yup.date().required('Start date is required'),
  endDateTime: yup.date().required('End date is required'),
});

export const updateBusinessSettingsSchema = yup.object({
  cancellationPolicyText: yup
    .string()
    .trim()
    .min(1, 'Cancellation policy text is required')
    .required(),
  minCancellationDays: yup
    .number()
    .integer()
    .min(0, 'Minimum cancellation days must be greater than or equal to 0')
    .required(),
  maxCancellationTimes: yup
    .number()
    .integer()
    .min(0, 'Maximum cancellation times must be greater than or equal to 0')
    .required(),
  bookAheadLimitDays: yup
    .number()
    .integer()
    .min(0, 'Book ahead limit days must be greater than or equal to 0')
    .required(),
  bookInAdvanceHours: yup
    .number()
    .integer()
    .min(0, 'Book in advance minutes must be greater than or equal to 0')
    .required(),
  minAppointmentPrice: yup
    .number()
    .min(0, 'Minimum appointment price must be greater than or equal to 0')
    .required(),
  stackDays: yup.boolean().required(),
  stackTimes: yup.boolean().required(),
  maxApptsPerDay: yup
    .number()
    .integer('Maximum appointments per day must be an integer')
    .min(0, 'Maximum appointments per day must be at least 0')
    .nullable(),
  allowCancellations: yup.boolean().required(),
  reschedulePolicy: yup
    .mixed<ReschedulePolicy>()
    .oneOf(Object.values(ReschedulePolicy))
    .required(),
  reschedulePolicyBehavior: yup
    .mixed<ReschedulePolicyBehavior>()
    .oneOf(Object.values(ReschedulePolicyBehavior))
    .required(),
  rescheduleFee: yup
    .number()
    .min(0, 'Reschedule fee must be greater than or equal to 0')
    .test(
      'max-decimal-places',
      'Reschedule fee can have at most 2 decimal places',
      value => testMaxDecimalPlaces(value || 0, 2),
    )
    .nullable()
    .when('reschedulePolicyBehavior', {
      is: ReschedulePolicyBehavior.FLAT_FEE,
      then: schema =>
        schema
          .required('Reschedule fee is required when charging a flat fee')
          .moreThan(0, 'Reschedule fee must be greater than 0'),
    }),
  cancellationWaitlistEnabled: yup.boolean().required(),
  cancellationWaitlistNotifyMode: yup
    .mixed<CancellationWaitlistNotifyMode>()
    .oneOf(Object.values(CancellationWaitlistNotifyMode))
    .required(),
  applyToExistingUsers: yup.boolean().required(),
});

export const baseUpsertBusinessServiceSchema = yup.object({
  serviceCategory: yup
    .mixed<ServiceCategory>()
    .oneOf(Object.values(ServiceCategory))
    .required(),
  name: yup.string().trim().min(1, "Name can't be empty").required(),
  description: yup
    .string()
    .trim()
    .min(1, "Description can't be empty")
    .required(),
  employeeIds: yup.array().of(yup.string()).required(),
  defaultPrice: yup
    .number()
    .min(0, 'Price must be greater than or equal to 0')
    .required(),
  defaultDepositPrice: yup
    .number()
    .min(0, 'Deposit price must be greater than or equal to 0')
    .transform(val => val || 0)
    .optional(),
  defaultDurationInMinutes: yup
    .number()
    .integer()
    .min(5, 'Duration must be greater than or equal to 5')
    .required()
    .test({
      test: val => val % 5 === 0,
      message: 'Duration must be in increment of 5 minutes',
    }),
  waiverName: yup.string().nullable().optional(),
  minimumAge: yup
    .number()
    .integer()
    .min(0, 'Minimum age must be at least 0')
    .required(),
  minimumAgeUnit: yup
    .mixed<'years' | 'months'>()
    .oneOf(['years', 'months'])
    .required(),
  requireMinorIdentification: yup
    .boolean()
    .required('Require minor ID upload setting is required'),
  requireMinorBirthCertificate: yup
    .boolean()
    .required('Require minor birth certificate upload setting is required'),
  requireMinorGuardianIdentification: yup
    .boolean()
    .required('Require minor guardian identification setting is required'),
  requireMinorGuardianPresent: yup
    .boolean()
    .required('Require minor guardian present setting is required'),
  aftercareLink: getUrlSchema('Aftercare link must be a valid URL')
    .nullable()
    .optional(),
  isGeneralPiercings: yup.boolean().when('serviceCategory', {
    is: ServiceCategory.PIERCING,
    then: schema => schema.required(),
    otherwise: schema => schema.nullable().optional(),
  }),
  piercingLocations: yup
    .array()
    .of(yup.number().integer())
    .test({
      test: (val, ctx) => {
        if (ctx.parent.serviceCategory !== ServiceCategory.PIERCING) {
          return true;
        }
        if (!val?.length) {
          ctx?.createError({
            message: 'Please select at least one piercing location',
          });
          return false;
        }
        if (!ctx.parent.isGeneralPiercings && val.length !== 1) {
          ctx?.createError({
            message: 'Only one piercing location is allowed',
          });
          return false;
        }
        return true;
      },
    })
    .required(),
});

export const ONLINE_BOOKINGS_BEHAVIOR = {
  DIRECT_BOOKINGS: 'DIRECT_BOOKINGS',
  BOOKING_REQUESTS: 'BOOKING_REQUESTS',
  NOT_ONLINE: 'NOT_ONLINE',
} as const;

export const serviceEmployeeCommissionFormSchema = yup.object({
  employeeId: yup.string().required(),
  enabled: yup.boolean().required(),
  rate: yup
    .number()
    .required()
    .when('enabled', {
      is: true,
      then: schema => schema.min(0).max(100),
    }),
});

export const upsertBusinessServiceFormSchema =
  baseUpsertBusinessServiceSchema.shape({
    onlineBookingsBehavior: yup
      .mixed<
        (typeof ONLINE_BOOKINGS_BEHAVIOR)[keyof typeof ONLINE_BOOKINGS_BEHAVIOR]
      >()
      .oneOf(Object.values(ONLINE_BOOKINGS_BEHAVIOR))
      .required(),
    enableCheckIn: yup.boolean().required(),
    employeeCommissions: yup
      .array()
      .of(serviceEmployeeCommissionFormSchema)
      .optional(),
  });

export const upsertBusinessServiceSchema = baseUpsertBusinessServiceSchema
  .shape({
    enableDirectBookings: yup.boolean().required(),
    enableBookingRequests: yup.boolean().required(),
    enableCheckIn: yup.boolean().required(),
    minimumAgeInMonths: yup.number().integer().min(0).optional(),
    intakeQuestions: yup
      .array()
      .of(
        yup.object({
          questionId: yup.string().required(),
          enabled: yup.boolean().required(),
        }),
      )
      .optional(),
    employeeCommissions: yup
      .array()
      .of(
        yup.object({
          employeeId: yup.string().required(),
          rate: yup.number().min(0).max(1).required(),
        }),
      )
      .optional(),
  })
  .test(
    'not-both',
    'Only one booking type can be enabled at a time',
    ({ enableDirectBookings, enableBookingRequests }) =>
      !(enableDirectBookings && enableBookingRequests),
  );

export const createBookingLinkSchema = yup.object({
  entityId: yup.string().required(),
  destination: yup
    .mixed<'instagram' | 'web'>()
    .oneOf(['instagram', 'web'])
    .required(),
  placeholder: yup.mixed().nullable().optional(),
});

export const updatePaymentsRatesSchema = yup.object({
  customerRate: yup.number().min(0).max(100).optional(),
  businessRate: yup.number().min(0).max(100).optional(),
  isBnplEnabled: yup.boolean().optional(),
  bnplFeePayer: yup
    .mixed<BnplFeePayer>()
    .oneOf(Object.values(BnplFeePayer))
    .optional(),
});

export const updateMinorSettingsSchema = yup.object({
  allowMinors: yup.boolean().required('Allow minors setting is required'),
  minimumTattooAgeInYears: yup
    .number()
    .integer('Minimum age must be a whole number')
    .min(0, 'Minimum age must be at least 0')
    .max(17, 'Minimum age must be less than 18')
    .required('Minimum age is required'),
  requireMinorIdentification: yup
    .boolean()
    .required('Require minor ID setting is required'),
  requireMinorBirthCertificate: yup
    .boolean()
    .required('Require minor birth certificate setting is required'),
  requireMinorGuardianIdentification: yup
    .boolean()
    .required('Require minor guardian identification setting is required'),
  requireMinorGuardianPresent: yup
    .boolean()
    .required('Require minor guardian present setting is required'),
});

export type BusinessTaxRatesPayload = yup.InferType<
  typeof businessTaxRatesSchema
>;

export type UpdateBusinessPayload = yup.InferType<typeof updateBusinessSchema>;
export type AddBusinessTripPayload = yup.InferType<
  typeof addBusinessTripSchema
>;
export type UpdateBusinessSettingsPayload = yup.InferType<
  typeof updateBusinessSettingsSchema
>;
export type UpsertBusinessServiceForm = yup.InferType<
  typeof upsertBusinessServiceFormSchema
>;
export type UpsertBusinessServicePayload = yup.InferType<
  typeof upsertBusinessServiceSchema
>;
export type UpsertBusinessServiceInitValues = Omit<
  UpsertBusinessServiceForm,
  'serviceCategory'
> & {
  serviceCategory?: ServiceCategory;
};
export type UpsertBusinessServiceDto = Omit<
  UpsertBusinessServicePayload,
  'minimumAge' | 'minimumAgeUnit'
> & {
  minimumAgeInMonths?: number;
};
export type CreateBookingLinkPayload = yup.InferType<
  typeof createBookingLinkSchema
>;

export type BusinessService = Services & {
  employeesToServices: EmployeesToServices[];
  piercingLocations: PiercingLocations[];
  waiver: Waivers | null;
  _count: {
    appointments: number;
  };
};

export type OnboardingResponse = {
  steps: ShopOnboardingSteps[];
  onboardings: ShopOnboardings[];
};

export type SkipOnboardingStepResponse = ShopOnboardings;
export type CompleteOnboardingStepResponse = ShopOnboardings;

export type ChiliPiperResponse = {
  routeId: string;
  url: string;
  redirect: {
    enabled: boolean;
    closeStrategy: string;
    timeout: number;
    phoneCallRedirectEnabled: boolean;
    redirectCallTimeoutStrategy: string;
    redirectCallTimeout: number;
  };
};

type TerminalProduct = {
  prices: {
    upfront: Stripe.Price;
    recurring: Stripe.Price;
  };
  image: string;
  name: string;
  description: string;
};

export type TerminalProducts = {
  fullTerminal: TerminalProduct;
  reader: TerminalProduct;
};

export type UpdatePaymentsRatesPayload = yup.InferType<
  typeof updatePaymentsRatesSchema
>;
export type UpdateMinorSettingsPayload = yup.InferType<
  typeof updateMinorSettingsSchema
>;

export type ServiceIntakeQuestion = Pick<
  IntakeQuestions,
  'questionId' | 'question' | 'description' | 'requiredForServices'
> & {
  enabled: boolean;
};
